import { UTCDate } from '@date-fns/utc';
import { TransactionType } from '@seapoint/server/enums';
import { AccountView, TransactionView } from '../../../types/entity-types';
import { SupportedCountryCurrency } from '../../../types/supported-country-currency';
import { getAmountWithCurrency } from '../../../utils/get-amount-with-currency';
import { ValueTone } from '../components/metrics/metric-card';
import { isInternalTransfer } from './is-internal-transfer';

type RevenueDataInput = {
  accounts: AccountView[];
  baseCurrency: SupportedCountryCurrency;
  currentPeriodStartDate: Date;
  currentPeriodEndDate: Date;
  previousPeriodStartDate: Date;
  previousPeriodEndDate: Date;
};

type TrendDirection = 'increasing' | 'decreasing' | 'default';

const EPSILON = 0.01; // Small threshold to avoid noisy percentage swings

/**
 * Computes revenue as total inflows (credit transactions) in the given timeframe, excluding refunds.
 * Returns formatted amount, period-over-period percentage change, and a trend direction.
 */
export const getRevenueData = ({
  accounts,
  baseCurrency,
  currentPeriodStartDate,
  currentPeriodEndDate,
  previousPeriodStartDate,
  previousPeriodEndDate,
}: RevenueDataInput) => {
  if (!accounts || accounts.length === 0) {
    return {
      formattedRevenue: getAmountWithCurrency({ amount: 0, currency: baseCurrency, isAccountBalance: true }),
      percentageChange: '0%',
      trend: 'default' as TrendDirection,
      valueTone: 'neutral' as const,
      revenueInBaseCurrency: 0,
    };
  }

  const allTransactions = accounts.flatMap((account) =>
    (account.transactions || []).map(({ fspDate, baseCurrencyAmount, type, isCredit, vendor }) => ({
      fspDate,
      baseCurrencyAmount,
      type,
      isCredit,
      vendor,
    })),
  );

  const inRange = ({ date, start, end }: { date: Date; start: Date; end: Date }) => date >= start && date <= end;

  const isRefundType = (type: TransactionType | undefined) =>
    type === TransactionType.REFUND || type === TransactionType.CARD_REFUND || type === TransactionType.CARD_CHARGEBACK;

  const computePeriodRevenue = (start: Date, end: Date) =>
    allTransactions
      .filter((t) => (t.fspDate ? inRange({ date: new UTCDate(t.fspDate), start, end }) : false))
      .filter((t) => !isInternalTransfer(t as TransactionView))
      .reduce((sum, t) => {
        const rawAmount = t.baseCurrencyAmount || 0;

        // Only include credit transactions (incoming money) that are not refunds
        if (t.isCredit && !isRefundType(t.type)) {
          return sum + Math.abs(rawAmount);
        }

        return sum;
      }, 0);

  // Current period revenue: credit transactions excluding refunds
  const currentPeriodRevenue = computePeriodRevenue(currentPeriodStartDate, currentPeriodEndDate);

  const formattedRevenue = getAmountWithCurrency({
    amount: currentPeriodRevenue,
    currency: baseCurrency,
    isAccountBalance: true,
  });

  // Previous period revenue for comparison
  const previousPeriodRevenue = computePeriodRevenue(previousPeriodStartDate, previousPeriodEndDate);

  let percentageChange = '0%';
  let trend: TrendDirection = 'default';

  if (previousPeriodRevenue < EPSILON) {
    if (currentPeriodRevenue < EPSILON) {
      percentageChange = '0%';
      trend = 'default';
    } else {
      // Jump from ~0 to some revenue: treat as 100% improvement
      percentageChange = '100%';
      trend = 'increasing';
    }
  } else {
    const delta = currentPeriodRevenue - previousPeriodRevenue; // + means better (more revenue), - means worse (less revenue)
    const percent = Math.abs((delta / previousPeriodRevenue) * 100);
    percentageChange = `${percent.toFixed(1)}%`;

    if (Math.abs(delta) < EPSILON) {
      trend = 'default';
    } else {
      trend = delta > 0 ? 'increasing' : 'decreasing';
    }
  }

  let valueTone: ValueTone;
  switch (trend) {
    case 'increasing':
      valueTone = 'positive' as const;
      break;
    case 'decreasing':
      valueTone = 'negative' as const;
      break;
    default:
      valueTone = 'neutral' as const;
      break;
  }

  return {
    formattedRevenue,
    percentageChange,
    trend,
    valueTone,
    revenueInBaseCurrency: currentPeriodRevenue,
  };
};
